import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Event {
  id: string;
  title: string;
  organizer_id: string;
  sport: string;
  event_type: 'tournament' | 'league';
  location: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  game_days?: string[];
  max_participants?: number;
  entry_fee?: number;
  description?: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  tournament_format?: string;
  league_format?: string;
  image_url?: string;
  group_rules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additional_rules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
    classification?: any;
  };
  
  category?: string;
  subcategory?: string;
  gender_category?: string;
  match_type?: string;
  registration_deadline?: string;
  distance?: string;
  court_surface?: string;
  prize_pool?: number;
  rules?: string;
  total_weeks?: number;
  matches_per_week?: number;
  // Computed/parsed fields
  subcategories?: { [key: string]: string[] };
  genderCategories?: string[];
  // Organizer info
  organizer_company_name?: string;
  organizer_full_name?: string;
  // Ladder league specific fields
  ladderConfig?: {
    winnerType?: 'first-place' | 'elimination';
    eliminationPhase?: 'octavos' | 'cuartos' | 'semifinal' | 'final';
    eliminationConfig?: {
      singleDateSchedule?: {
        date?: string;
        startTime?: string;
        endTime?: string;
        courts?: number;
      };
      phaseSchedules?: Record<string, {
        date?: string;
        startTime?: string;
        endTime?: string;
      }>;
    };
  };
  address?: string;
  postal_code?: string;
}

export function useEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      // First, get all events
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('is_published', true)
        .order('start_date', { ascending: true });

      if (eventsError) throw eventsError;

      // Get unique organizer user IDs from events
      const organizerUserIds = [...new Set((eventsData || []).map(event => event.organizer_id))];
      
      // Query organizer_public_profiles directly using user_id (no RLS issues)
      const { data: organizersData } = await supabase
        .from('organizer_public_profiles')
        .select('user_id, company_name')
        .in('user_id', organizerUserIds);

      // Create map: user_id -> company_name
      const userIdToCompanyNameMap = new Map(
        (organizersData || []).map((org: any) => [org.user_id, org.company_name])
      );

      // Transform events with organizer information
      const eventsWithOrganizers = (eventsData || []).map(event => {
        const companyName = userIdToCompanyNameMap.get(event.organizer_id);
        
        return {
          ...event,
          organizer_company_name: companyName,
          organizer_full_name: undefined
        };
      });
      
      // Transform the data to match the expected format
      const transformedEvents = eventsWithOrganizers.map(event => {
        // Extract time fields explicitly
        const eventData = event as any;
        const startTime = eventData.start_time || null;
        const endTime = eventData.end_time || null;
        const gameDays = eventData.game_days || null;
        
        // Parse subcategories and categories from the rules JSON field
        const subcategories: { [key: string]: string[] } = {};
        const genderCategories: string[] = [];
        let maxParticipantsFromRules = event.max_participants;
        
        // Parse group_rules from JSON to proper object
        let parsedGroupRules: { 
          setsPerMatch?: number; 
          goldenPoint?: boolean; 
          superTieBreak?: boolean; 
          maxGamesPerSet?: number; 
        } | undefined;
        
        if (event.group_rules) {
          try {
            parsedGroupRules = typeof event.group_rules === 'string' 
              ? JSON.parse(event.group_rules) 
              : event.group_rules;
          } catch (error) {
            console.error('Error parsing group_rules:', error);
          }
        }
        
        // Parse additional_rules from JSON to proper object
        let parsedAdditionalRules: {
          timing?: {
            warmupMinutes?: number;
            restMinutes?: number;
            sideChangeSeconds?: number;
          };
          conduct?: {
            toleranceMinutes?: number;
          };
          classification?: any;
        } | undefined;
        
        if (event.additional_rules) {
          try {
            parsedAdditionalRules = typeof event.additional_rules === 'string' 
              ? JSON.parse(event.additional_rules) 
              : event.additional_rules;
          } catch (error) {
            console.error('Error parsing additional_rules:', error);
          }
        }
        
        // Parse rules JSON if it exists
        let ladderConfig: Event['ladderConfig'];
        
        if (event.rules) {
          try {
            const rulesData = typeof event.rules === 'string' ? JSON.parse(event.rules) : event.rules;
            console.log('Event rules data:', event.title, rulesData);
            
            // Extract ladderConfig for escalera leagues
            if (rulesData.ladderConfig) {
              ladderConfig = {
                winnerType: rulesData.ladderConfig.winnerType,
                eliminationPhase: rulesData.ladderConfig.eliminationPhase,
                eliminationConfig: rulesData.ladderConfig.eliminationConfig
              };
            }
            
            // Fallback: extraer de roundRobinConfig para ligas round-robin
            if (!ladderConfig?.winnerType && rulesData.roundRobinConfig) {
              const rrConfig = rulesData.roundRobinConfig;
              if (rrConfig.winnerType) {
                ladderConfig = {
                  winnerType: rrConfig.winnerType,
                  eliminationPhase: rrConfig.eliminationPhase,
                  eliminationConfig: rrConfig.eliminationConfig
                };
              }
            }
            
            // Extract gender categories from selectedGenders
            if (rulesData.selectedGenders && Array.isArray(rulesData.selectedGenders)) {
              genderCategories.push(...rulesData.selectedGenders);
            }
            
            // Fallback: extract from ladderConfig.tennisCategories for tennis leagues
            if (genderCategories.length === 0 && rulesData.ladderConfig?.tennisCategories) {
              const tc = rulesData.ladderConfig.tennisCategories;
              if (tc.selectedGenders && Array.isArray(tc.selectedGenders)) {
                genderCategories.push(...tc.selectedGenders);
              }
              if (tc.subcategoryDetails && typeof tc.subcategoryDetails === 'object') {
                Object.entries(tc.subcategoryDetails).forEach(([gender, details]) => {
                  if (Array.isArray(details)) {
                    subcategories[gender] = details.map((d: any) => d.name || d);
                    let total = 0;
                    details.forEach((d: any) => {
                      if (d.maxRegistrations) total += d.maxRegistrations;
                    });
                    if (total > 0) maxParticipantsFromRules = (maxParticipantsFromRules || 0) + total;
                  }
                });
              }
            }
            
            // Extract subcategories from genderCategories object
            if (rulesData.genderCategories && typeof rulesData.genderCategories === 'object') {
              Object.entries(rulesData.genderCategories).forEach(([gender, categories]) => {
                if (Array.isArray(categories) && categories.length > 0) {
                  // Extract the 'category' field from each object in the array
                  subcategories[gender] = categories.map((cat: any) => cat.category || cat);
                } else if (categories && typeof categories === 'object') {
                  // Handle case where categories might be an object with category keys
                  const categoryKeys = Object.keys(categories);
                  if (categoryKeys.length > 0) {
                    subcategories[gender] = categoryKeys;
                  }
                }
              });
            }
            
            // Calculate max participants from genderCategories configuration
            if (rulesData.genderCategories && typeof rulesData.genderCategories === 'object') {
              let totalCapacity = 0;
              Object.values(rulesData.genderCategories).forEach((categories: any) => {
                if (Array.isArray(categories)) {
                  categories.forEach((cat: any) => {
                    if (cat && typeof cat === 'object' && typeof cat.maxParticipants === 'number') {
                      totalCapacity += cat.maxParticipants;
                    }
                  });
                }
              });
              if (totalCapacity > 0) {
                maxParticipantsFromRules = totalCapacity;
              }
            }
          } catch (error) {
            console.error('Error parsing rules JSON for event:', event.title, error);
          }
        }
        
        // Fallback to legacy category/subcategory fields if rules parsing didn't work
        if (genderCategories.length === 0 && event.category && event.subcategory) {
          genderCategories.push(event.category);
          subcategories[event.category] = event.subcategory.split(',').map(s => s.trim());
        }
        
        return {
          ...event,
          start_time: startTime,
          end_time: endTime,
          game_days: gameDays,
          subcategories,
          genderCategories,
          max_participants: maxParticipantsFromRules,
          leagueFormat: event.league_format,
          tournamentFormat: event.tournament_format,
          matchType: event.match_type,
          isLeague: event.event_type === 'league',
          group_rules: parsedGroupRules,
          additional_rules: parsedAdditionalRules,
          image_url: event.image_url,
          ladderConfig
        };
      });
      
      setEvents(transformedEvents);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const refetch = () => {
    fetchEvents();
  };

  return { events, loading, error, refetch };
}

export function useEventsByType(eventType: 'tournament' | 'league') {
  const { events, loading, error, refetch } = useEvents();
  
  const filteredEvents = events.filter(event => event.event_type === eventType);
  
  return { events: filteredEvents, loading, error, refetch };
}

export function useEventsBySport(sport: string) {
  const { events, loading, error, refetch } = useEvents();
  
  const filteredEvents = events.filter(event => 
    event.sport.toLowerCase() === sport.toLowerCase()
  );
  
  return { events: filteredEvents, loading, error, refetch };
}

export function useUpcomingEventsByType(eventType: 'tournament' | 'league') {
  const { events, loading, error, refetch } = useEvents();
  
  const filteredEvents = events.filter(event => 
    event.event_type === eventType && 
    event.status === 'upcoming'
  );
  
  return { events: filteredEvents, loading, error, refetch };
}